<template>
  <div>
    <!-- Floating Button -->
    <CButton
      color="primary"
      class="chat-toggle"
      :class="{ dragging: isDragging }"
      :style="toggleStyle"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="onToggleClick"
    >
      <!-- Icon -->
      <span class="icon">🤖</span>
      <span class="tooltip">Ask AI</span>
    </CButton>

    <!-- Chat Popup -->
    <CCard v-if="isOpen" class="chat-popup shadow-lg" :style="popupStyle">
      <CCardHeader
        class="chat-header d-flex justify-content-between align-items-center"
      >
        <!-- Left Section -->
        <div class="d-flex align-items-center">
          <!-- Avatar -->
          <div class="avatar">🤖</div>

          <!-- Title -->
          <div class="ml-2">
            <div class="title">AI Assistant</div>
            <small class="status text-success">Online</small>
          </div>
        </div>

        <!-- Right Section -->
        <CButton size="sm" color="primary" variant="ghost" @click="toggleChat">
          ✕
        </CButton>
      </CCardHeader>

      <!-- Body -->
      <CCardBody class="p-0">
        <ChatBox />
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import ChatBox from "./ChatBox.vue";

const TOGGLE_SIZE = 60;
const POPUP_WIDTH = 350;
const POPUP_HEIGHT = 450;
const DRAG_THRESHOLD = 5;

export default {
  components: { ChatBox },
  data() {
    return {
      isOpen: false,
      isDragging: false,
      hasDragged: false,
      // null until the user drags the button for the first time,
      // so it keeps using the default CSS (bottom-right) position
      position: null,
      dragStart: { x: 0, y: 0 },
      dragOffset: { x: 0, y: 0 },
    };
  },
  computed: {
    toggleStyle() {
      if (!this.position) return {};
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
        right: "auto",
        bottom: "auto",
      };
    },
    popupStyle() {
      if (!this.position) return {};

      const margin = 10;
      let left = this.position.x + TOGGLE_SIZE - POPUP_WIDTH;
      let top = this.position.y - POPUP_HEIGHT - margin;

      // Flip below the button if there isn't enough room above
      if (top < margin) {
        top = this.position.y + TOGGLE_SIZE + margin;
      }

      const maxLeft = window.innerWidth - POPUP_WIDTH - margin;
      const maxTop = window.innerHeight - POPUP_HEIGHT - margin;
      left = Math.min(Math.max(left, margin), Math.max(maxLeft, margin));
      top = Math.min(Math.max(top, margin), Math.max(maxTop, margin));

      return {
        left: `${left}px`,
        top: `${top}px`,
        right: "auto",
        bottom: "auto",
      };
    },
  },
  methods: {
    toggleChat() {
      this.isOpen = !this.isOpen;
    },
    onToggleClick() {
      if (this.hasDragged) {
        this.hasDragged = false;
        return;
      }
      this.toggleChat();
    },
    getEventPoint(event) {
      if (event.touches && event.touches.length) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      return { x: event.clientX, y: event.clientY };
    },
    startDrag(event) {
      const point = this.getEventPoint(event);
      const rect = event.currentTarget.getBoundingClientRect();

      this.isDragging = true;
      this.hasDragged = false;
      this.dragStart = point;
      this.dragOffset = {
        x: point.x - rect.left,
        y: point.y - rect.top,
      };

      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
      window.addEventListener("touchmove", this.onDrag, { passive: false });
      window.addEventListener("touchend", this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      if (event.cancelable) event.preventDefault();

      const point = this.getEventPoint(event);

      if (
        !this.hasDragged &&
        (Math.abs(point.x - this.dragStart.x) > DRAG_THRESHOLD ||
          Math.abs(point.y - this.dragStart.y) > DRAG_THRESHOLD)
      ) {
        this.hasDragged = true;
      }

      const margin = 8;
      const x = Math.min(
        Math.max(point.x - this.dragOffset.x, margin),
        window.innerWidth - TOGGLE_SIZE - margin
      );
      const y = Math.min(
        Math.max(point.y - this.dragOffset.y, margin),
        window.innerHeight - TOGGLE_SIZE - margin
      );

      this.position = { x, y };
    },
    stopDrag() {
      this.isDragging = false;

      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
      window.removeEventListener("touchmove", this.onDrag);
      window.removeEventListener("touchend", this.stopDrag);

      if (this.hasDragged && this.position) {
        const margin = 8;
        const centerX = this.position.x + TOGGLE_SIZE / 2;
        const snappedX =
          centerX < window.innerWidth / 2
            ? margin
            : window.innerWidth - TOGGLE_SIZE - margin;

        this.position = { x: snappedX, y: this.position.y };
      }
    },
  },
  beforeUnmount() {
    this.stopDrag();
  },
};
</script>

<style scoped>
.chat-popup {
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 350px;
  height: 450px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}
.chat-toggle {
  position: fixed;
  bottom: 35px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b8c9f8, #aff4db);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  transition: all 0.3s ease;
  cursor: grab;
  touch-action: none;
}

.chat-toggle.dragging {
  cursor: grabbing;
  transition: none;
}

/* Icon */
.icon {
  font-size: 26px;
}

/* Hover Effect */
.chat-toggle:hover {
  transform: scale(1.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

/* Pulse Animation */
.chat-toggle::after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(78, 115, 223, 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  70% {
    transform: scale(1.6);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

/* Badge */
.badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: red;
  color: white;
  font-size: 10px;
  padding: 3px 6px;
  border-radius: 50%;
}

.tooltip {
  position: absolute;
  right: 70px;
  background: #333;
  color: #fff;
  padding: 5px 8px;
  border-radius: 4px;
  font-size: 12px;
  opacity: 0;
  transition: 0.2s;
  white-space: nowrap;
}

.chat-toggle:hover .tooltip {
  opacity: 1;
}

</style>
